'use client';

import { useEffect, useRef, useState } from 'react';

export type ImageAdjustVariant =
  | 'grayscale-image'
  | 'invert-image-colors'
  | 'sepia-image'
  | 'image-brightness'
  | 'image-contrast';

const meta: Record<
  ImageAdjustVariant,
  { badge: string; title: string; filter: (value: number) => string; slider: boolean }
> = {
  'grayscale-image': {
    badge: 'Grayscale',
    title: 'Convert an image to grayscale',
    filter: () => 'grayscale(1)',
    slider: false,
  },
  'invert-image-colors': {
    badge: 'Invert',
    title: 'Invert image colors',
    filter: () => 'invert(1)',
    slider: false,
  },
  'sepia-image': {
    badge: 'Sepia',
    title: 'Apply a sepia tone',
    filter: () => 'sepia(1)',
    slider: false,
  },
  'image-brightness': {
    badge: 'Brightness',
    title: 'Adjust image brightness',
    filter: (v) => `brightness(${v})`,
    slider: true,
  },
  'image-contrast': {
    badge: 'Contrast',
    title: 'Adjust image contrast',
    filter: (v) => `contrast(${v})`,
    slider: true,
  },
};

export default function BrowserImageAdjuster({ variant }: { variant: ImageAdjustVariant }) {
  const config = meta[variant];
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [value, setValue] = useState(1);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [sourceName, setSourceName] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setSourceName(file.name.replace(/\.[^.]+$/, ''));
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.filter = config.filter(value);
    ctx.drawImage(image, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      // Release the previous export before replacing it: this ran on every slider move.
      setDownloadUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(blob);
      });
    }, 'image/png');
  }, [image, value, config]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{config.badge}</span>
        <h2 className="tool-live-title">{config.title}</h2>
        <p className="tool-live-subtitle">
          Upload an image and it is processed entirely in your browser. Nothing is uploaded to a
          server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>
          {config.slider && image ? (
            <label className="tool-live-field tool-live-field-full">
              <span>
                {config.badge}: {Math.round(value * 100)}%
              </span>
              <input
                max={2}
                min={0}
                onChange={(event) => setValue(Number(event.target.value))}
                step={0.05}
                type="range"
                value={value}
              />
            </label>
          ) : null}
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${sourceName ?? variant}-${variant}.png`}
                href={downloadUrl}
              >
                Download PNG
              </a>
            ) : null}
          </div>
          {imageUrl ? null : (
            <p className="tool-live-panel-note">Pick an image to see the result.</p>
          )}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
